import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field } from '../components/Field.js';
import { Nav } from '../components/Nav.js';

type Role = 'proche' | 'senior';

const STORAGE_KEY_ROLE = 'cadran.role';
const STORAGE_KEY_PRENOM = 'cadran.prenom';
const STORAGE_KEY_EMAIL = 'cadran.email';

export function Inscription() {
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>('proche');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    localStorage.setItem(STORAGE_KEY_ROLE, role);
    localStorage.setItem(STORAGE_KEY_PRENOM, prenom);
    localStorage.setItem(STORAGE_KEY_EMAIL, email);
    navigate('/onboarding');
  }

  const ROLES: { id: Role; label: string; desc: string }[] = [
    { id: 'proche', label: 'Je suis un proche', desc: 'Fils, fille, petit-enfant, aidant...' },
    { id: 'senior', label: 'Je suis le senior', desc: "Je configure l'écran pour moi-même." },
  ];

  return (
    <>
      <Nav />
      <main>
        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            padding: '64px 56px 96px',
            alignItems: 'start',
          }}
        >
          {/* Pitch éditorial sticky */}
          <div style={{ position: 'sticky', top: '96px' }}>
            <div className="eyebrow" style={{ marginBottom: '14px' }}>
              Étape 1 / 6
            </div>
            <h1
              className="display"
              style={{ fontSize: 'clamp(48px, 5vw, 72px)', margin: '0 0 24px', maxWidth: '16ch' }}
            >
              Bienvenue dans la <em>famille Cadran</em>.
            </h1>
            <p
              className="serif"
              style={{
                fontStyle: 'italic',
                fontWeight: 300,
                fontSize: '19px',
                lineHeight: 1.4,
                color: 'var(--ink-soft)',
                marginBottom: '32px',
              }}
            >
              Cinq minutes pour configurer. Une présence qui dure.
            </p>
            <div
              style={{
                background: 'var(--paper-2)',
                border: '1px solid var(--hairline-soft)',
                borderLeft: '3px solid var(--terracotta)',
                borderRadius: '0 8px 8px 0',
                padding: '20px 24px',
              }}
            >
              <p className="small" style={{ margin: 0, lineHeight: 1.6 }}>
                Vous créez un compte pour <b>piloter l'écran</b> depuis votre téléphone. Votre
                proche n'a aucun compte, aucun mot de passe — il voit juste l'écran.
              </p>
            </div>
          </div>

          {/* Carte formulaire */}
          <div className="card">
            <div className="eyebrow" style={{ marginBottom: '20px' }}>
              Votre rôle
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '28px',
              }}
            >
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`role-${r.id}`}
                  onClick={() => setRole(r.id)}
                  style={{
                    padding: '18px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${role === r.id ? 'var(--terracotta)' : 'var(--hairline)'}`,
                    background: role === r.id ? 'oklch(0.97 0.016 50)' : 'var(--paper)',
                    boxShadow: role === r.id ? '0 0 0 4px oklch(0.665 0.124 42 / 0.10)' : 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'border-color .15s ease, box-shadow .15s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '6px',
                    }}
                  >
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `2px solid ${role === r.id ? 'var(--terracotta)' : 'var(--hairline)'}`,
                        background: role === r.id ? 'var(--terracotta)' : 'transparent',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: 'Fraunces Variable, serif',
                        fontWeight: 500,
                        fontSize: '16px',
                        color: 'var(--ink)',
                      }}
                    >
                      {r.label}
                    </span>
                  </div>
                  <p className="small" style={{ margin: '0 0 0 26px', lineHeight: 1.4 }}>
                    {r.desc}
                  </p>
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="field-row">
                <Field
                  id="prenom"
                  label="Prénom"
                  type="text"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  required
                  autoComplete="given-name"
                />
                <Field
                  id="nom"
                  label="Nom"
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  autoComplete="family-name"
                />
              </div>
              <Field
                id="email"
                label="Adresse e-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                hint="Vous recevrez un lien magique pour vous connecter — pas de mot de passe à retenir."
              />
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{ width: '100%', marginTop: '8px' }}
                disabled={!prenom.trim() || !email.trim()}
              >
                Continuer →
              </button>
            </form>
          </div>
        </div>
      </main>
    </>
  );
}
